import { useState, useCallback } from 'react';
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, type Hex } from 'viem';
import {
  ERC20_ABI,
  PRESALE_ABI,
  PRESALE_ADDRESS,
  USDT_ADDRESS,
  USDT_DECIMALS,
} from '../lib/contracts';
import { referralApi } from '../api/referral';

type Step = 'idle' | 'approving' | 'approve-confirming' | 'buying' | 'buy-confirming' | 'attributing' | 'done' | 'error';

interface BuyResult {
  step: Step;
  errorMessage: string | null;
  txHash: Hex | null;
  /** Trigger the full flow for a USDT amount (e.g. "150") + optional referral code from URL/localStorage. */
  buy: (usdtAmount: string, referralCode?: string) => Promise<void>;
  reset: () => void;
}

/**
 * Buy flow:
 *   1. Read current USDT allowance for the presale contract
 *   2. If allowance < amount: send approve(amount), wait for receipt
 *   3. Send buyTokens(amount)
 *   4. Wait for receipt
 *   5. POST attribution intent to backend with txHash + referralCode + walletAddress
 *
 * This wraps the wagmi hooks into a single linear state machine for the Buy page.
 */
export function useBuyFlow(): BuyResult {
  const { address } = useAccount();
  const [step, setStep] = useState<Step>('idle');
  const [errorMessage, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: USDT_ADDRESS,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, PRESALE_ADDRESS] : undefined,
    query: { enabled: !!address },
  });

  const { writeContractAsync } = useWriteContract();

  const waitForTx = useCallback(async (hash: Hex) => {
    // The user-facing UI will reflect step changes; the actual block confirmation
    // is owned by useWaitForTransactionReceipt within the component using this hook.
    return hash;
  }, []);

  const reset = () => {
    setStep('idle');
    setError(null);
    setTxHash(null);
  };

  const buy = useCallback(
    async (usdtAmount: string, referralCode?: string) => {
      if (!address) {
        setError('Connect your wallet first');
        setStep('error');
        return;
      }
      try {
        setError(null);

        const amount = parseUnits(usdtAmount, USDT_DECIMALS);
        const currentAllowance = (allowance as bigint | undefined) ?? 0n;

        // Step 1: approve if needed
        if (currentAllowance < amount) {
          setStep('approving');
          const approveHash = await writeContractAsync({
            address: USDT_ADDRESS,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [PRESALE_ADDRESS, amount],
          });
          setStep('approve-confirming');
          await waitForTx(approveHash);
          // After approval, give the chain a beat to update allowance reads:
          await new Promise((r) => setTimeout(r, 1500));
          await refetchAllowance();
        }

        // Step 2: call buyTokensWithUSDT
        setStep('buying');
        const buyHash = await writeContractAsync({
          address: PRESALE_ADDRESS,
          abi: PRESALE_ABI,
          functionName: 'buyTokensWithUSDT',
          args: [amount],
        });
        setTxHash(buyHash);
        setStep('buy-confirming');

        // Step 3: attribution intent — best-effort, don't block the cycle if it fails.
        setStep('attributing');
        if (referralCode) {
          try {
            await referralApi.attribute({
              txHash: buyHash,
              referralCode,
              walletAddress: address,
            });
          } catch (err) {
            // Non-fatal; just log
            console.warn('Attribution intent submission failed', err);
          }
        }

        setStep('done');
      } catch (err: unknown) {
        const e = err as { shortMessage?: string; message?: string };
        setError(e.shortMessage ?? e.message ?? 'Transaction failed');
        setStep('error');
      }
    },
    [address, allowance, writeContractAsync, refetchAllowance, waitForTx],
  );

  return { step, errorMessage, txHash, buy, reset };
}

/** A standalone hook for the component to confirm a tx receipt (separate so it remounts cleanly). */
export function useTxReceipt(hash: Hex | undefined) {
  return useWaitForTransactionReceipt({ hash });
}
